import { api } from './api';

const MAX_SIDE = 256;

function toBase64(buf: ArrayBuffer): string {
  let s = '';
  const b = new Uint8Array(buf);
  for (let i = 0; i < b.length; i += 0x8000) s += String.fromCharCode(...b.subarray(i, i + 0x8000));
  return btoa(s);
}

/**
 * Upload a logo and get back its public URL. Raster images are scaled down to fit 256×256 and
 * re-encoded as WebP (transparency kept) so stored files stay tiny; SVGs are sent as they are.
 */
export async function uploadImage(file: File): Promise<string> {
  let type = file.type;
  let buf: ArrayBuffer;
  if (type === 'image/svg+xml') {
    buf = await file.arrayBuffer();
  } else if (type.startsWith('image/')) {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, MAX_SIDE / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(bitmap.width * scale));
    canvas.height = Math.max(1, Math.round(bitmap.height * scale));
    canvas.getContext('2d')!.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise<Blob | null>((r) => canvas.toBlob(r, 'image/webp', 0.9));
    // Very old browsers can't encode WebP; fall back to PNG.
    const out =
      blob?.type === 'image/webp'
        ? blob
        : await new Promise<Blob | null>((r) => canvas.toBlob(r, 'image/png'));
    if (!out) throw new Error('Could not read that image.');
    type = out.type;
    buf = await out.arrayBuffer();
  } else {
    throw new Error('Choose a PNG, JPG, WebP, GIF or SVG image.');
  }
  if (buf.byteLength > 256 * 1024) throw new Error('Images must be 256 KB or smaller.');
  const r = await api<{ url: string }>('/media', {
    body: { contentType: type, data: toBase64(buf) },
  });
  return r.url;
}
